import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { IconZap } from '../components/Icons';
import { C } from '../theme/space';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate('/');
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === 'NO_BROADCASTER_ACCESS') {
        setError(data.error || 'Your organization does not have Broadcaster access. Contact your AcceleratorX administrator.');
      } else if (err.response?.status === 403) {
        setError(data?.error || 'Access denied. Your account may be deactivated.');
      } else {
        setError(data?.error || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = (name) => ({
    ...s.input,
    border: `1px solid ${focused === name ? C.borderHover : C.border}`,
    boxShadow: focused === name ? `0 0 0 3px rgba(99,102,241,0.12)` : 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  });

  return (
    <div style={s.wrapper}>
      {/* Orbital ring — decorative */}
      <div style={s.ring1} />
      <div style={s.ring2} />

      <motion.div
        style={s.card}
        initial={{ opacity: 0, y: 32, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        {/* Top accent bar */}
        <div style={s.accentBar} />

        {/* Logo */}
        <motion.div
          style={s.logoRow}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.4 }}
        >
          <div style={s.logoMark}>
            <IconZap size={18} color="#fff" />
          </div>
          <span style={s.logoText}>AcceleratorX</span>
        </motion.div>

        <motion.p
          style={s.subtitle}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.22, duration: 0.4 }}
        >
          Bot Management Console
        </motion.p>

        <motion.form
          onSubmit={handleSubmit}
          style={s.form}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
        >
          <div style={s.fieldGroup}>
            <label style={s.label}>Email Address</label>
            <input
              style={inputStyle('email')}
              type="email"
              placeholder="you@company.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              onFocus={() => setFocused('email')}
              onBlur={() => setFocused(null)}
              required
            />
          </div>

          <div style={s.fieldGroup}>
            <label style={s.label}>Password</label>
            <input
              style={inputStyle('password')}
              type="password"
              placeholder="••••••••"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              onFocus={() => setFocused('password')}
              onBlur={() => setFocused(null)}
              required
            />
          </div>

          {error && (
            <motion.p
              style={s.error}
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
            >
              {error}
            </motion.p>
          )}

          <button style={{ ...s.btn, opacity: loading ? 0.75 : 1 }} type="submit" disabled={loading}>
            {loading ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <span style={s.spinner} /> Signing in...
              </span>
            ) : (
              'Sign In'
            )}
          </button>
        </motion.form>
      </motion.div>
    </div>
  );
}

const s = {
  wrapper: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    position: 'relative',
    overflow: 'hidden',
  },
  // Decorative orbital rings
  ring1: {
    position: 'absolute',
    width: 520,
    height: 520,
    borderRadius: '50%',
    border: '1px solid rgba(99,102,241,0.07)',
    pointerEvents: 'none',
  },
  ring2: {
    position: 'absolute',
    width: 340,
    height: 340,
    borderRadius: '50%',
    border: '1px solid rgba(56,189,248,0.06)',
    pointerEvents: 'none',
  },
  card: {
    background: 'rgba(6, 12, 28, 0.92)',
    border: '1px solid rgba(99,102,241,0.2)',
    borderRadius: 20,
    padding: '44px 48px',
    width: 400,
    position: 'relative',
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    boxShadow: '0 0 60px rgba(99,102,241,0.08), 0 24px 80px rgba(0,0,0,0.6)',
    overflow: 'hidden',
  },
  accentBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 2,
    background: 'linear-gradient(90deg, #6366f1, #a855f7, #38bdf8)',
    borderRadius: '20px 20px 0 0',
  },
  logoRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    marginBottom: 8,
    marginTop: 4,
  },
  logoMark: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: 'linear-gradient(135deg, #6366f1, #818cf8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 14px rgba(99,102,241,0.4)',
  },
  logoText: {
    fontSize: 22,
    fontWeight: 700,
    color: '#e2e8f0',
    letterSpacing: -0.5,
  },
  subtitle: {
    color: C.textMuted,
    textAlign: 'center',
    fontSize: 13,
    marginBottom: 32,
    letterSpacing: 0.2,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    fontSize: 11,
    fontWeight: 600,
    color: C.textMuted,
    marginBottom: 7,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  input: {
    padding: '12px 14px',
    borderRadius: 9,
    background: 'rgba(2, 5, 16, 0.8)',
    color: C.text,
    fontSize: 14,
    outline: 'none',
    width: '100%',
  },
  error: {
    color: C.danger,
    fontSize: 13,
    padding: '10px 14px',
    background: 'rgba(248,113,113,0.08)',
    border: '1px solid rgba(248,113,113,0.2)',
    borderRadius: 8,
  },
  btn: {
    marginTop: 4,
    padding: '13px',
    borderRadius: 9,
    border: 'none',
    background: 'linear-gradient(135deg, #6366f1, #818cf8)',
    color: '#fff',
    fontWeight: 600,
    fontSize: 15,
    cursor: 'pointer',
    boxShadow: '0 4px 20px rgba(99,102,241,0.35)',
    transition: 'opacity 0.2s',
    letterSpacing: 0.2,
  },
  spinner: {
    display: 'inline-block',
    width: 14,
    height: 14,
    border: '2px solid rgba(255,255,255,0.25)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
  },
};
